import { useEffect, useRef } from "react";

interface VersionInfo {
  version: string;
  buildTime?: string | null;
  commit?: string | null;
}

const VERSION_URL = `${process.env.PUBLIC_URL}/version.json`;
const CHECK_INTERVAL_MS = 2 * 60 * 1000; // 2분마다 확인

async function fetchVersion(): Promise<VersionInfo | null> {
  try {
    const res = await fetch(`${VERSION_URL}?_=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * 배포(빌드) 시 갱신되는 public/version.json 을 주기적으로 조회해
 * 최초 로딩 시점과 다른 버전이 감지되면 onNewVersion 을 1회 호출합니다.
 * (Jenkins 배포 후 새로고침 유도용)
 */
export function useVersionCheck(onNewVersion: () => void) {
  const onNewVersionRef = useRef(onNewVersion);
  onNewVersionRef.current = onNewVersion;

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    let cancelled = false;
    let initialVersion: string | null = null;
    let notified = false;

    const check = async () => {
      const info = await fetchVersion();
      if (!info || cancelled) return;

      if (initialVersion === null) {
        initialVersion = info.version;
        return;
      }

      if (!notified && info.version !== initialVersion) {
        notified = true;
        onNewVersionRef.current();
      }
    };

    check();
    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);

    const handleVisibility = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);
}
